import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Skeleton } from '@sorare/core/src/atoms/animations/Skeleton';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BodyM, LabelS } from '@sorare/core/src/atoms/typography';
import { ActiveUserAvatar } from '@sorare/core/src/components/user/ActiveUserAvatar';
import { FollowButton } from '@sorare/core/src/components/user/FollowButton';
import { MyClubLink } from '@sorare/core/src/components/user/MyClubLink';
import { UserName } from '@sorare/core/src/components/user/UserName';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';

import { Block } from '../Block';
import type {
  RepresentedByQuery,
  RepresentedByQueryVariables,
} from './__generated__/index.graphql';

export type CampaignWinner = {
  represents: string;
  represented_by: string;
  icon: string;
};

const REPRESENTED_BY_QUERY = gql`
  query RepresentedByQuery($user: String!, $sport: Sport!) {
    userById(id: $user) {
      slug
      followed {
        slug
      }
      userSportProfile: anyUserSportProfile(sport: $sport) {
        id
        clubName
        clubBadge {
          id
          pictureUrl
        }
        background {
          id
          pictureUrl
        }
      }
      ...UserName_publicUserInfoInterface
      ...ActiveUserAvatar_user
      ...MyClubLink_publicUserInfoInterface
    }
  }
  ${UserName.fragments.user}
  ${ActiveUserAvatar.fragments.user}
  ${MyClubLink.fragments.user}
` as TypedDocumentNode<RepresentedByQuery, RepresentedByQueryVariables>;

const StyledHorizontal = styled(Horizontal)`
  align-items: self-start;
`;
const StyledMyClubLink = styled(MyClubLink)`
  transition: opacity 0.2s ease-in-out;
  &:hover {
    opacity: 0.5;
  }
`;
const AvatarSkeleton = styled(Skeleton)`
  width: calc(5 * var(--unit) + 2px);
  height: calc(5 * var(--unit) + 2px);
  border-radius: 100%;
`;
const TextSkeleton = styled(Skeleton)`
  width: calc(16 * var(--unit));
  height: var(--double-unit);
`;
const ButtonSkeleton = styled(Skeleton)`
  width: calc(10 * var(--unit));
  height: calc(5 * var(--unit));
  border-radius: 2em;
`;

type Props = {
  campaignWinner: CampaignWinner;
};

export const RepresentedBy = ({ campaignWinner }: Props) => {
  const { sport } = useSportContext();
  const { loading, data } = useQuery(REPRESENTED_BY_QUERY, {
    variables: {
      user: idFromObject(campaignWinner.represented_by),
      sport,
    },
    skip: !campaignWinner.represented_by,
  });

  const { userById: user } = data || {};

  return (
    <Block
      title={
        <FormattedMessage
          id="Profile.RepresentedBy.representedBy"
          defaultMessage="Represented by"
        />
      }
      icon={<img src={campaignWinner.icon} alt="" width="16" height="16" />}
      backgroundImage={user?.userSportProfile?.background?.pictureUrl}
    >
      {!!loading && (
        <SBHorizontal>
          <StyledHorizontal>
            <AvatarSkeleton />
            <Vertical gap={0.5}>
              <TextSkeleton />
              <TextSkeleton />
            </Vertical>
          </StyledHorizontal>
          <ButtonSkeleton />
        </SBHorizontal>
      )}
      {!!user && (
        <SBHorizontal>
          <StyledMyClubLink user={user}>
            <StyledHorizontal>
              <ActiveUserAvatar rounded size={5} user={user} />
              <Vertical gap={0}>
                <BodyM bold>
                  <UserName user={user} />
                </BodyM>
                {!!user.userSportProfile && (
                  <Horizontal gap={0.5}>
                    {!!user.userSportProfile.clubBadge && (
                      <img
                        src={user.userSportProfile.clubBadge.pictureUrl}
                        alt=""
                        width="16"
                        height="16"
                      />
                    )}
                    <LabelS color="var(--c-nd-600)">
                      {user.userSportProfile.clubName}
                    </LabelS>
                  </Horizontal>
                )}
              </Vertical>
            </StyledHorizontal>
          </StyledMyClubLink>
          <FollowButton
            size="medium"
            subscribable={{ __typename: 'User', slug: user.slug }}
            initialSubscription={user.followed}
          />
        </SBHorizontal>
      )}
    </Block>
  );
};
